/*globals views*/
import React, { useContext, useEffect, useState, lazy } from "react";
import styled from "styled-components";
import TargetContext from "./TargetContext";
import SubscriberInformation from "./SubscriberInformation";
import ActionsContext from "common/ActionsContext";
import SubscriberDetailHelpModal from "./Help";
import { Header } from "common/Header";
import CheckLicenseFirst from "common/CheckLicenseFirst";
import LazyInView from "common/LazyInView";

const SubscriberAnalytics = lazy(() => import("./SubscriberAnalytics"));
const DPIAnalytics = lazy(() => import("./DPIAnalytics"));
const SubscriberFlows = lazy(() => import("./SubscriberFlows"));
const LiveMetrics = lazy(() => import("./LiveMetrics"));

const ViewContainer = styled.div`
  min-height: 50vh;
  &.card {
    height: 100%;
  }
`;

const SubscriberAnalyticsWrapper = styled.div`
  min-height: 2000px;
`;

const DPIAnalyticsWrapper = styled.div`
  min-height: 830px;
`;

const ContextMenu = ({ setLiveMode,onBeforeLoad }) => {
  const actions = useContext(ActionsContext);
  return (
    <div className="context-menu">
      <a
        data-loading-effect="pulse"
        data-toggle="cardloading"
        title="Help"
        onClick={() => actions.send("open-help-details")}
      >
        <i className="material-icons">
          <small>help_outline</small>
        </i>
      </a>
      <a
        onClick={() => {
          setLiveMode(undefined);
          onBeforeLoad();
          actions.send("do-load-details");
        }}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Refresh"
      >
        <i className="material-icons">
          <small>refresh</small>
        </i>
      </a>
      <a
        onClick={() => window.prepareAndPrint()}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Print"
        className="margin-l-15"
      >
        <i className="material-icons">
          <small>print</small>
        </i>
      </a>
    </div>
  );
};

export const applyParams = (child, params = {}) =>
  React.isValidElement(child) ? React.cloneElement(child, params) : child;

const Panel = React.memo(({ children, ...props }) => (
  <div className="card">
    <div className={`body ${props.className || ""}`}>
      {children({ ...props })}
    </div>
  </div>
));
Panel.displayName = "Panel";

const PanelNoStyle = React.memo(({ children, ...props }) => (
    <div>
      {children({ ...props })}
    </div>
));
PanelNoStyle.displayName = "PanelNoStyle";

const LiveModePanel = React.memo(({ children, ...props }) => (
    <>
      {children({ ...props })}
    </>
));
LiveModePanel.displayName = "LiveModePanel";

const SubcriberDetail = ({ addr, subsId, dpiEnabled = false, returnView, timePeriod }) => {
  const [liveMode, setLiveMode] = useState();
  const [subscriberInformationReady, setSubscriberInformationReady] = useState(false);
  const [ipAddressSelected, setIpAddressSelected] = useState(null);


  useEffect(() => {
    $('html')[0].scrollTop = 0
  }, [])

  useEffect(() => {
    if (!liveMode) {
      $("#live-switch").prop("checked", false);
    }
  }, [liveMode]);

  useEffect(() => {
    setSubscriberInformationReady(false);
  }, [addr, subsId])

  function subscriberFlowsPanel(props) {
    return (
      <Panel name="flows" {...props}>
        {(props) => (
          <LazyInView>
            <SubscriberFlows
              {...props}
              setLiveMode={(value) => {
                setLiveMode(value);
              }}
              dpiEnabled={dpiEnabled}
            />
          </LazyInView>
        )}
      </Panel>
    );
  }

  return (
    <div className="viewSubsDetail">
      <Header
        title="Subscriber dashboard"
        backButton={returnView !== null}
        contextMenu={<ContextMenu
          setLiveMode={setLiveMode}
          onBeforeLoad={() => setSubscriberInformationReady(false)}
        />}
        returnUrl={returnView}
      />

      <ViewContainer className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <TargetContext addr={addr} subsId={subsId}>
          <Panel name="information">
            {(props) => (
              <SubscriberInformation
                {...props}
                liveMode={liveMode}
                setLiveMode={setLiveMode}
                onLeavingPage={() => setLiveMode(false)}
                dpiEnabled={dpiEnabled}
                onLoaded={() => setSubscriberInformationReady(true)}
                onBeforeLoad={() => setSubscriberInformationReady(false)}
                timePeriod={timePeriod}
                onIpAddressSelected={(ipAddress) => setIpAddressSelected(ipAddress)}
              />
            )}
          </Panel>
          <PanelNoStyle>
          {(props) => {
            if(!subscriberInformationReady){
              return <></>
            }
            
            return (
              <>
                <LiveModePanel {...props}>
                  {(props) => (
                    <LazyInView>
                      <LiveMetrics
                        {...props}
                        liveMode={liveMode}
                        setLiveMode={(value) => {
                          setLiveMode(value);
                        }}
                        dpiEnabled={dpiEnabled}
                      />
                    </LazyInView>
                  )}
                </LiveModePanel>
                <Panel name="analytics" {...props}>
                  {(props) => (
                    <SubscriberAnalyticsWrapper>
                      <LazyInView>
                        <SubscriberAnalytics
                          {...props}
                          ipAddressSelected={ipAddressSelected}
                        />
                      </LazyInView>
                    </SubscriberAnalyticsWrapper>
                  )}
                </Panel>
                {dpiEnabled === true ? (
                  <Panel name="dpi" {...props}>
                    {(props) => (
                      <DPIAnalyticsWrapper>
                        <LazyInView>
                          <DPIAnalytics {...props} />
                        </LazyInView>
                      </DPIAnalyticsWrapper>
                    )}
                  </Panel>
                ) : null}
                {subscriberFlowsPanel(props)}
              </>
            );
          }}
          </PanelNoStyle>
        </TargetContext>
        <SubscriberDetailHelpModal />
      </ViewContainer>
    </div>
  );
};

const VerifiedSubcriberDetail = ({ ...args }) => (
  <CheckLicenseFirst {...args}>
    <SubcriberDetail {...args} />
  </CheckLicenseFirst>
);

export default VerifiedSubcriberDetail;
